import type { KeystrokeEventI } from "@Common/types/bo/applications/applications";
import PauseIcon from "@mui/icons-material/Pause";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import ReplayIcon from "@mui/icons-material/Replay";
import {
	Box,
	Button,
	ButtonGroup,
	IconButton,
	Slider,
	Typography,
} from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";

const IGNORED_KEYS = new Set([
	"Shift",
	"Control",
	"Alt",
	"Meta",
	"CapsLock",
	"Tab",
	"Escape",
	"ArrowLeft",
	"ArrowRight",
	"ArrowUp",
	"ArrowDown",
	"Home",
	"End",
	"PageUp",
	"PageDown",
	"Insert",
	"Delete",
	"F1",
	"F2",
	"F3",
	"F4",
	"F5",
	"F6",
	"F7",
	"F8",
	"F9",
	"F10",
	"F11",
	"F12",
]);

const SPEED_OPTIONS = [1, 2, 4, 8] as const;

export const reconstructText = (
	events: KeystrokeEventI[],
	upToIndex: number,
): string => {
	let text = "";
	for (const event of events.slice(0, upToIndex + 1)) {
		const key = event.key;
		if (key === "Backspace") {
			text = text.slice(0, -1);
		} else if (key === "Enter") {
			text += "\n";
		} else if (!IGNORED_KEYS.has(key) && key.length === 1) {
			text += key;
		}
	}
	return text;
};

export const findEventIndexAtTime = (
	events: KeystrokeEventI[],
	targetTimestamp: number,
): number => {
	if (events.length === 0) return -1;
	let lo = 0;
	let hi = events.length - 1;
	let result = -1;
	while (lo <= hi) {
		const mid = Math.floor((lo + hi) / 2);
		const midTimestamp = events[mid]?.timestamp ?? 0;
		if (midTimestamp <= targetTimestamp) {
			result = mid;
			lo = mid + 1;
		} else {
			hi = mid - 1;
		}
	}
	return result;
};

const formatTime = (ms: number): string => {
	const totalSeconds = Math.round(ms / 1000);
	const mins = Math.floor(totalSeconds / 60);
	const secs = totalSeconds % 60;
	if (mins > 0) return `${mins}m ${secs}s`;
	return `${secs}s`;
};

export const KeystrokeReplayPlayer = ({
	events,
}: {
	events: KeystrokeEventI[];
}) => {
	const [isPlaying, setIsPlaying] = useState(false);
	const [playbackSpeed, setPlaybackSpeed] =
		useState<(typeof SPEED_OPTIONS)[number]>(1);
	const [elapsedMs, setElapsedMs] = useState(0);

	const speedRef = useRef(playbackSpeed);
	const elapsedRef = useRef(0);
	const sliderRef = useRef<HTMLSpanElement>(null);

	const firstEvent = events[0] as KeystrokeEventI;
	const lastEvent = events[events.length - 1] as KeystrokeEventI;
	const startTimestamp = firstEvent.timestamp;
	const totalDurationMs = lastEvent.timestamp - startTimestamp;

	// Derive currentIndex from elapsedMs — single source of truth
	const currentIndex =
		elapsedMs > 0 || isPlaying
			? findEventIndexAtTime(events, startTimestamp + elapsedMs)
			: -1;
	const displayedText = reconstructText(events, currentIndex);

	// Keep speed ref in sync
	useEffect(() => {
		speedRef.current = playbackSpeed;
	}, [playbackSpeed]);

	// Helper to directly update slider DOM for smooth visual progress
	const updateSliderDOM = useCallback((pct: number) => {
		if (!sliderRef.current) return;
		const thumb = sliderRef.current.querySelector(
			".MuiSlider-thumb",
		) as HTMLElement | null;
		const track = sliderRef.current.querySelector(
			".MuiSlider-track",
		) as HTMLElement | null;
		if (thumb) thumb.style.left = `${pct}%`;
		if (track) track.style.width = `${pct}%`;
	}, []);

	// Main playback loop — runs whenever isPlaying becomes true
	useEffect(() => {
		if (!isPlaying) return;

		let prevTime = performance.now();
		let rafId: number;

		const tick = () => {
			const now = performance.now();
			const delta = now - prevTime;
			prevTime = now;

			const newElapsed = Math.min(
				elapsedRef.current + delta * speedRef.current,
				totalDurationMs,
			);
			elapsedRef.current = newElapsed;

			// Update React state (may be batched/deferred by React)
			setElapsedMs(newElapsed);

			// Direct DOM update guarantees smooth slider on every frame
			const pct =
				totalDurationMs > 0 ? (newElapsed / totalDurationMs) * 100 : 0;
			updateSliderDOM(pct);

			rafId = requestAnimationFrame(tick);
		};

		rafId = requestAnimationFrame(tick);
		return () => cancelAnimationFrame(rafId);
	}, [isPlaying, totalDurationMs, updateSliderDOM]);

	// Stop playback when elapsed reaches the end
	useEffect(() => {
		if (isPlaying && totalDurationMs > 0 && elapsedMs >= totalDurationMs) {
			setIsPlaying(false);
		}
	}, [elapsedMs, isPlaying, totalDurationMs]);

	const play = useCallback(() => {
		if (totalDurationMs > 0 && elapsedRef.current >= totalDurationMs) {
			elapsedRef.current = 0;
			setElapsedMs(0);
			updateSliderDOM(0);
		}
		setIsPlaying(true);
	}, [totalDurationMs, updateSliderDOM]);

	const pause = useCallback(() => {
		setIsPlaying(false);
	}, []);

	const reset = useCallback(() => {
		setIsPlaying(false);
		elapsedRef.current = 0;
		setElapsedMs(0);
		updateSliderDOM(0);
	}, [updateSliderDOM]);

	const seekToProgress = useCallback(
		(_: Event | React.SyntheticEvent, value: number | number[]) => {
			const numValue = typeof value === "number" ? value : (value[0] ?? 0);
			const pct = numValue / 100;
			const newElapsed = pct * totalDurationMs;
			setIsPlaying(false);
			elapsedRef.current = newElapsed;
			setElapsedMs(newElapsed);
		},
		[totalDurationMs],
	);

	const progressPercent =
		totalDurationMs > 0 ? (elapsedMs / totalDurationMs) * 100 : 0;

	return (
		<Box
			sx={{
				border: 1,
				borderColor: "divider",
				borderRadius: 1,
				p: 2,
				mt: 1,
				mb: 1,
				maxWidth: 600,
			}}
		>
			{/* Text display */}
			<Box
				sx={{
					fontFamily: "monospace",
					fontSize: "0.875rem",
					bgcolor: "grey.50",
					border: 1,
					borderColor: "grey.300",
					borderRadius: 1,
					p: 1.5,
					minHeight: 60,
					maxHeight: 200,
					overflow: "auto",
					whiteSpace: "pre-wrap",
					wordBreak: "break-word",
					position: "relative",
				}}
			>
				{displayedText}
				{isPlaying && (
					<Box
						component="span"
						sx={{
							display: "inline-block",
							width: "2px",
							height: "1em",
							bgcolor: "text.primary",
							ml: "1px",
							animation: "blink 1s step-end infinite",
							"@keyframes blink": {
								"50%": { opacity: 0 },
							},
						}}
					/>
				)}
			</Box>

			{/* Progress slider */}
			<Slider
				ref={sliderRef}
				value={progressPercent}
				onChange={seekToProgress}
				size="small"
				sx={{
					mt: 1,
					mb: 0,
					"& .MuiSlider-thumb": { transition: "none" },
					"& .MuiSlider-track": { transition: "none" },
				}}
			/>

			{/* Controls */}
			<Box
				sx={{
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					mt: 0,
				}}
			>
				<Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
					<IconButton
						size="small"
						onClick={isPlaying ? pause : play}
						color="primary"
						aria-label={isPlaying ? "Pause" : "Play"}
					>
						{isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
					</IconButton>
					<IconButton size="small" onClick={reset} aria-label="Reset">
						<ReplayIcon />
					</IconButton>
				</Box>

				<Typography variant="caption" color="text.secondary">
					{formatTime(elapsedMs)} / {formatTime(totalDurationMs)}
				</Typography>

				<ButtonGroup size="small" variant="outlined">
					{SPEED_OPTIONS.map((speed) => (
						<Button
							key={speed}
							onClick={() => setPlaybackSpeed(speed)}
							variant={playbackSpeed === speed ? "contained" : "outlined"}
							sx={{ minWidth: 36, px: 0.5 }}
						>
							{speed}×
						</Button>
					))}
				</ButtonGroup>
			</Box>
		</Box>
	);
};
